import {IDomainObject, TExtractFieldFn} from "core/form/formUtils";
import {fieldStyles} from "core/form/createForm";
import {observable, ObservableMap, action} from "mobx";
import {DomainAncestor} from "core/common/domain/DomainAncestor";
import {DATATYPE, default as MdField} from "core/metamodel/mdField";
import MdEntity from "core/metamodel/mdEntity";
import isFunction = require("lodash/isFunction");
import SyntheticEvent = React.SyntheticEvent;
import {ValidationMessage} from "core/common/domain/commonDomain";
import {ValidationResult} from "core/form/validate";
import {MdFieldValidation} from "core/metamodel/mdField";
import {LovItem} from "core/metamodel/mdEntity";



type SetFieldValueFnType = (dataObject: any, value: any, entity: MdEntity) => void;
type GetFieldValueFnType = (dataObject: any) => any;

type ValidatorNameOrSpec = MdFieldValidation | string;

type ValidatorFunctionType = (mdField: MdField) => ValidatorNameOrSpec[];


export interface FieldObjectType {
  name: string,
  setFieldValueFn: SetFieldValueFnType;
  getFieldValueFn: GetFieldValueFnType;
  textLabel: string;
  mdField: MdField;
  validators?: MdFieldValidation[];
  props?: PropsObjectType;
  showValidation?: boolean;
  errorText: string;
  valid?: boolean;
  touched?: boolean;
  disableValidation?: boolean;
  handleValidation?: (validationResultStruct: {validationResult?: ValidationResult, showValidation?: boolean}, caller: string) => void;
}

export interface PropsObjectType {
  mdField?: MdField;
  fullWidth?: boolean;
  name?: string;
  style?: any;
  hintStyle?: any;
  inputStyle?: any;
  errorStyle?: any;
  numberFormat?: string;
  errorText?: string;
  labelStyle?: any;
  floatingLabelStyle?: any;
  label?: string;
  floatingLabelText?: string;
  hintText?: string;
  maxLength?: number;
  clearable?: boolean;
  searchable?: boolean;
  onFocus?: React.FocusEventHandler<any>;
  onBlur?: React.FocusEventHandler<any>;
  onCheck?: React.ReactEventHandler<any>;
  onChange?: any;
  options?: LovItem[];
  value?: any;
  checked?: any;
  validating?: boolean;
  autoComplete?: string;
}

export enum FIELDDEFDISPLAYTYPE {
  textArea = "textArea",
  checkbox = "checkbox",
  radioButton = "radioButton"
}

export enum FIELDDEFSIZE {
  small = "small",
  big = "big",
  huge = "huge"
}


export interface FieldDefinitionType {
  name: string;
  entityName?: string;
  fieldName?: string;
  mdField?: MdField;
  size?: FIELDDEFSIZE;
  displayType?: FIELDDEFDISPLAYTYPE;
  setFieldValueFn?: SetFieldValueFnType;
  getFieldValueFn?: GetFieldValueFnType;
  label?: string;
  validators?: ValidatorFunctionType | ValidatorNameOrSpec[];
  style?: any;
  numberFormat?: string;
  usePlaceholder?: boolean;
  omitNullOption?: boolean;
}

export interface DefinitionType {
  formName: string;
  fields: FieldDefinitionType[];
  onCommit?: Function;
  nestedForms?: any;
  nested?: boolean;
  closedOnNew?: boolean;

}

export default class Form<D extends DomainAncestor> {


  public formName: string;
  public formDefinition: DefinitionType;

  @observable public fields: {[key in keyof D]: FieldObjectType};

  @observable public $open: boolean;

  public $pristineObject: D;

  public onCommit: Function;


  public nestedForms: any;
  public nested: boolean;



  constructor (formName: string, formDefinition: DefinitionType) {
    this.formName = formName;
    this.formDefinition = formDefinition;
  }

  isFormValid(): boolean {
    if (this.fields) {
      for(let field of Object.values(this.fields)) {
        if (field.valid === false) return false
      }
    }
    return true;
  }

  getField(fieldName: keyof D): FieldObjectType {
    return this.fields[fieldName];
  }

  showAllValidations(): void {
    for(let field of Object.values<FieldObjectType>(<any>this.fields)) {
      field.showValidation = true;
      field.props.errorText = field.errorText;
    }
  }

  validateForm(dataObject: D, errorArray?: ValidationMessage[]): boolean {
    let res = this.isFormValid();
    if (!res) {
      this.showAllValidations();
      if (errorArray) errorArray.push({message: 'Form has errors, please correct them first'});
      return false;
    } else {
      if (this.onCommit) {
        this.onCommit(dataObject);
      }
      return true;
    }
  };


  getValue(dataObject: D, field: FieldObjectType|string): any {
    let fieldObj: FieldObjectType = (typeof field === 'string' || field instanceof String) ? this.getField(field as keyof D) : field;

    return fieldObj.mdField.initMuiInput( (fieldObj.getFieldValueFn) ? fieldObj.getFieldValueFn(dataObject) : dataObject[fieldObj.name] );

    // if (fieldObj.getFieldValueFn) {
    //   return fieldObj.getFieldValueFn(dataObject);
    // } else {
    //   let val = dataObject[fieldObj.name];
    //   return fieldObj.mdField.initMuiInput(val);
    // }

  }

  updateAllFieldsPropValue(dataObject: D) {
    console.debug('_updateFieldsValue on %O', dataObject);
    if (this.fields && typeof this.fields === 'object') {
      // nastaveni value
      for (let field of Object.values(this.fields)) {
        this.updateFieldPropValue(field, dataObject);
      }
    }
  }


  updateFieldsPropValue(dataObject: D, ... fieldNames: string[]): void {
    if (!this.fields) return;

    for(let fieldName of fieldNames) {
      let field = this.fields[fieldName as any];
      if (!field) throw new Error("Field " + fieldName + " not found on form " + this.formName);
      this.updateFieldPropValue(field, dataObject);
    }
  }

  updateFieldPropValue(field: FieldObjectType, dataObject: D): void {
    field.disableValidation = true;
    if (field.mdField.dataType === DATATYPE.BOOLEAN) {
      field.props.checked = this.getValue(dataObject, field);
    } else {
      field.props.value = this.getValue(dataObject, field);
    }
  }




  setValue(entity: MdEntity, dataObject: D, field: FieldObjectType, value: any): void {
    field.disableValidation = false;
    if (field.setFieldValueFn) {
      field.setFieldValueFn(dataObject, value, entity);
    } else {
      dataObject[field.name] = (value === '') ? null : value;
    }
  }

  initForm(entity: MdEntity, entities: ObservableMap<MdEntity>, dataObject: D, rootObject: DomainAncestor, extractFieldFn: TExtractFieldFn<D>) {

    this.fields = this.setupFields(dataObject, rootObject, entity, entities, extractFieldFn);
    this.onCommit = this.formDefinition.onCommit;
    this.nestedForms = this.formDefinition.nestedForms;
    this.nested = this.formDefinition.nested;

    if (this.$open == null && !this.formDefinition.closedOnNew) this.$open = dataObject.$new;
    if (this.$open && !this.$pristineObject) this.$pristineObject = dataObject.cloneShallow();
  }


  createField(fieldDef: FieldDefinitionType, dataObject: D, rootObject: IDomainObject, entity: MdEntity, entities: ObservableMap<MdEntity>, extractFieldFn: TExtractFieldFn<D>): FieldObjectType {
    //console.debug('createForm: field: %o on entity %o', field, entity);
    const resolvedEntity: MdEntity = (fieldDef.entityName) ? entities.get(fieldDef.entityName) : entity;
    const mdFieldName = (fieldDef.fieldName) ? fieldDef.fieldName : fieldDef.name;

    let mdField: MdField = (extractFieldFn) ? extractFieldFn(dataObject, fieldDef) : ( (resolvedEntity) ? resolvedEntity.getField(mdFieldName) : null );
    if (!mdField) mdField = fieldDef.mdField;
    if (!mdField) throw new Error("MdField " + mdFieldName + " not found on form " + this.formDefinition.formName + " on entity " + (resolvedEntity ? resolvedEntity.id : 'null') + ", neither on (client) fields definition");
    const fieldDefGroup: any =  (fieldDef.size) ? fieldStyles[fieldDef.size] : fieldStyles.small;

    let isTextField = false;

    // TextField style overrides (zmenseni)
    let styleDef: any = fieldDefGroup.default;
    if (fieldDef.displayType === FIELDDEFDISPLAYTYPE.textArea) {
      styleDef = fieldDefGroup.textArea;
    } else if (fieldDef.displayType === FIELDDEFDISPLAYTYPE.checkbox) {
      styleDef = fieldDefGroup.checkbox;
    } else if (fieldDef.displayType === FIELDDEFDISPLAYTYPE.radioButton) {
      styleDef = fieldDefGroup.radioButton;
    } else if (mdField.valueSourceType) {
      styleDef = fieldDefGroup.styledSelect;
    } else {
      // zatim zbytek textField
      styleDef = fieldDefGroup.textField;
      isTextField = true;
    }

    let rawValidations: ValidatorNameOrSpec[] = (isFunction(fieldDef.validators)) ? fieldDef.validators(mdField) : fieldDef.validators;
    let validations: MdFieldValidation[] = (rawValidations) ? rawValidations.map(v => (typeof v === 'string') ? {validation: v} : v) : null;
    if (mdField.validations && mdField.validations.length) {
      validations = (validations && validations.length) ? validations.concat(mdField.validations.slice()) : mdField.validations.slice();
    }

    let fieldObject: FieldObjectType = {
      name: fieldDef.name,
      setFieldValueFn: fieldDef.setFieldValueFn,
      getFieldValueFn: fieldDef.getFieldValueFn,
      textLabel: fieldDef.label || mdField.formLabelActive,
      mdField: mdField,
      validators: validations,
      showValidation: false,
      valid: true,
      errorText: null
    };

    const propsObject: PropsObjectType = {
      mdField: mdField,
      fullWidth: true,
      name: fieldDef.name,
      style: Object.assign({}, styleDef.style, fieldDef.style),
      hintStyle: styleDef.hintStyle,
      inputStyle: styleDef.inputStyle,
      errorStyle: styleDef.errorStyle,
      numberFormat: (fieldDef.numberFormat) ? fieldDef.numberFormat : mdField.numberFormat,
      errorText: null,
      validating: false,
      options: null
    };

    if (fieldDef.displayType === FIELDDEFDISPLAYTYPE.checkbox || fieldDef.displayType === FIELDDEFDISPLAYTYPE.radioButton) {
      propsObject.labelStyle = styleDef.labelStyle;
    }
    if (isTextField) {
      propsObject.autoComplete = fieldDef.name;
    }

    fieldObject.props = propsObject;

    // tucne a hvezdicka pro povinne
    let editLabel = fieldDef.label || mdField.formLabelActive;
    let floatingLabelStyle = Object.assign({}, styleDef.floatingLabelStyle);
    if (fieldObject.validators && fieldDef.size !== FIELDDEFSIZE.huge && fieldObject.validators.find(v => v.validation === 'IsRequired')) {
      editLabel = editLabel + " *";
    }
    if (!fieldDef.usePlaceholder) propsObject.floatingLabelStyle = floatingLabelStyle;

    let cbName = this.formDefinition.formName + "." + fieldObject.name;

    switch (mdField.dataType) {
      case DATATYPE.BOOLEAN:
        propsObject.label = editLabel;
        propsObject.checked = null;
        propsObject.value = null; // potrebuju sledovat porad, viz MultiUpdateFieldForm
        break;
      default:
        propsObject.value = null;
        if (fieldDef.usePlaceholder) {
          propsObject.hintText = editLabel;
        } else {
          propsObject.floatingLabelText = editLabel;
        }
    }

    if (mdField.maxLength != null) {
      propsObject.maxLength = mdField.maxLength;
    }

    if (mdField.hasLocalValueSource()) {
      let valueSourceEntity = entities.get(mdField.valueSource);
      if (valueSourceEntity && valueSourceEntity.lovItems) {
        propsObject.options = observable.shallowArray(valueSourceEntity.lovItems.slice());
        if (fieldDef.displayType !== FIELDDEFDISPLAYTYPE.radioButton && !fieldDef.omitNullOption) propsObject.options.unshift({value: null, label: '---'});
        propsObject.searchable = (valueSourceEntity.lovItems.length > 4);
      }
      propsObject.clearable = false;

    }


    fieldObject = observable(fieldObject);


    // event handlery, ktere uzaviraji fieldObject do closure az po klonu observable

    switch (mdField.dataType) {
      case DATATYPE.BOOLEAN:
        fieldObject.props.onCheck = action(cbName + " onCheck()", (evt: SyntheticEvent<any>) => {
          //let {dataObject, rootObject} = this.props;
          let value = evt.target.checked;

          // kvuli mobx3 - kdyz dela object observable tak ho klonuje, takze fieldObject a propsObject closure ma jinej, starej objekt
          //let fieldObject2: FieldObjectType = dataObject.getFormField(this.formDefinition.formName, fieldDef.name as any);


          this.setValue(entity, dataObject, fieldObject, value);
          fieldObject.props.checked = value;
          rootObject.$dirty = true;
          dataObject.$dirty = true;
          //setDataAction(rootObject, 'createForm field ' + fieldDef.name + ' onCheck()');
          console.debug('createForm field ' + fieldDef.name + ' onCheck(): value = %O, on object %O, props = %O', value, dataObject, fieldObject.props);
        });
        break;
      default:
        fieldObject.props.onChange = action(cbName + " onChange()", (evt: SyntheticEvent<any>) => {
          //let {dataObject, rootObject} = this.props;
          let value = (evt != null && evt.target) ? evt.target.value : evt;

          // kvuli mobx3 - kdyz dela object observable tak ho klonuje, takze fieldObject a propsObject closure ma jinej, starej objekt
          //let fieldObject2: FieldObjectType = dataObject.getFormField(this.formDefinition.formName, fieldDef.name as any);


          this.setValue(entity, dataObject, fieldObject, value);
          fieldObject.props.value = value;
          rootObject.$dirty = true;
          dataObject.$dirty = true;
          console.debug('createForm field ' + fieldDef.name + ' onChange(): value = %O, on object %O, props = %O', value, dataObject, fieldObject.props);

        });
    }

    propsObject.onBlur = (evt) => {
      // kvuli mobx3 - kdyz dela object observable tak ho klonuje, takze fieldObject a propsObject closure ma jinej, starej objekt
      //let fieldObject2: FieldObjectType = dataObject.getFormField(this.formDefinition.formName, fieldDef.name as any);

      fieldObject.showValidation = true;
      //this.props.setDataAction(this.props.rootObject, 'createForm field ' + fieldObject.name + ' onBlur()');
    };


    fieldObject.handleValidation = ({validationResult, showValidation}: {validationResult: ValidationResult, showValidation: boolean}, caller: string) => {

      // kvuli mobx3 - kdyz dela object observable tak ho klonuje, takze fieldObject a propsObject closure ma jinej, starej objekt
      //let fieldObject2: FieldObjectType = dataObject.getFormField(this.formDefinition.formName, fieldDef.name as any);

      //console.log("%c handleValidation (%s) : field = %s, validationResult = %O, showValidation = %s", 'background-color: lightblue', caller, fieldObject.name, validationResult, showValidation);

      fieldObject.showValidation = showValidation;
      fieldObject.valid = validationResult.valid;
      fieldObject.errorText = validationResult.error;

      fieldObject.props.errorText =  (showValidation) ? fieldObject.errorText : null;

      if (validationResult.valid && validationResult.error) {
        if (fieldObject.props.errorStyle) {
          fieldObject.props.errorStyle = { ...fieldObject.props.errorStyle, color: 'orange'};
        } else {
          fieldObject.props.errorStyle = {color: 'orange'};
        }
      }

      fieldObject.props.validating = false;
    };

    return fieldObject;
  }

  setupFields(dataObject: D, rootObject: IDomainObject, entity: MdEntity, entities: ObservableMap<MdEntity>, extractFieldFn: TExtractFieldFn<D>): {[key in keyof D]: FieldObjectType} {
    console.log('setupFields for ' + this.formDefinition.formName);

    const fields: any = this.formDefinition.fields.reduce((acc, fieldDef) => {
      acc[fieldDef.name] = this.createField(fieldDef, dataObject, rootObject, entity, entities, extractFieldFn);
      return acc;
    }, {});

    return fields;
  }



}



// WEBPACK FOOTER //
// ./src/core/form/form.ts